import React, { useState } from 'react';
import {
  Dialog,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { keyframes } from '@mui/material/styles';

interface InactivityWarningPopupProps {
  open: boolean;
  onClose: () => void;
  daysLeft?: number;
}

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulseAnimation = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

export const InactivityWarningPopup: React.FC<InactivityWarningPopupProps> = ({
  open,
  onClose,
  daysLeft = 60,
}) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem('inactivityWarningDismissed', 'true');
    }
    onClose();
  };

  // Determine status color based on days left
  const getStatusColor = () => {
    if (daysLeft > 30) return '#4caf50'; // Green
    if (daysLeft > 7) return '#ff9800'; // Orange
    return '#d32f2f'; // Red
  };

  const statusColor = getStatusColor();

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          animation: `${slideIn} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)`,
          overflow: 'hidden',
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
        },
      }}
    >
      {/* Header with Gradient Background */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #EE7D11 0%, #EE7D11 100%)',
          padding: '32px 24px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: 'absolute',
            top: -40,
            right: -40,
            width: 150,
            height: 150,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -60,
            left: -60,
            width: 200,
            height: 200,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          }}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                animation: `${pulseAnimation} 2s ease-in-out infinite`,
              }}
            >
              <LockOutlinedIcon sx={{ color: 'white', fontSize: 32 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  margin: 0,
                  letterSpacing: '0.5px',
                }}
              >
                Account Security
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontWeight: 500,
                }}
              >
                Important Notice
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Content Section */}
      <Box sx={{ padding: '32px 24px' }}>
        {/* Message Content */}
        <Box sx={{ marginBottom: '24px' }}>
          <Typography
            variant="h6"
            sx={{
              marginBottom: '12px',
              fontWeight: 700,
              color: '#212121',
              fontSize: '18px',
            }}
          >
            Account Security Notice
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#555',
              lineHeight: 1.8,
              marginBottom: '12px',
              fontSize: '15px',
            }}
          >
            Your account will be automatically locked after <strong>60 days of inactivity</strong> for security purposes.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#777',
              lineHeight: 1.7,
              fontSize: '14px',
              backgroundColor: '#f5f5f5',
              padding: '12px 14px',
              borderRadius: '8px',
              borderLeft: '4px solid #EE7D11',
            }}
          >
            💡 <strong>Tip:</strong> Log in regularly to keep your account active. Check the header to see your remaining days.
          </Typography>
        </Box>

        {/* Checkbox Section */}
        <Box
          sx={{
            padding: '16px',
            backgroundColor: '#fafafa',
            borderRadius: '8px',
            marginBottom: '20px',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                size="small"
                sx={{
                  '&.Mui-checked': {
                    color: '#EE7D11',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: '13px', color: '#333' }}>
                Don't show this message again
              </Typography>
            }
          />
        </Box>

        {/* Action Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            onClick={handleClose}
            variant="contained"
            size="large"
            sx={{
              background: 'linear-gradient(135deg, #EE7D11 0%, #EE7D11 100%)',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '12px',
              boxShadow: '0 4px 15px #EE7D11',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 8px 25px #EE7D11',
                transform: 'translateY(-2px)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            I Understand
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
